<script setup lang="ts">
import ConnectionsLayout from '@/modules/shared/layouts/connections-layout.vue'
import ConnectionsHero from '../../components/connections-hero.vue'
import ConnectionGroupCard from '../../components/connection-group-card.vue'
import ConnectionInvestorRow from '../../components/connection-investor-row.vue'
import WelcomeComponent from '../../components/groups/welcome-component.vue'
import WaitingState from '../../components/groups/waiting-state.vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import { VButton, VIcon, VLoadingSection, VModal, VPopUpModal } from '@/modules/shared/components'
import VAvatar from '@/modules/shared/components/v-avatar.vue'
import VBadge from '@/modules/shared/components/v-badge.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useConnectionStore } from '../../stores/connection-store'

import { useModal } from '@/modules/shared/composables/use-modal'
import type { Connection, GroupCandidateExpanded } from '../../types'
import AccreditationBadge from '@/modules/screening/component/AccreditationBadge.vue'
import ScreeningCheckWrapper from '@/modules/screening/component/CheckWrapper.vue'
import { get } from 'lodash'
import { useCandidateStore } from '../../stores/candidate-store'
import { AccreditationStatuses, type Status as ScreeningStatus } from '@/modules/screening/types'
import { INVESTMENT_TYPE_LABELS } from '../../types'

const TYPE_LABELS = INVESTMENT_TYPE_LABELS

const route = useRoute()
const connectionStore = useConnectionStore()
const workspaceStore = useWorkspaceStore()
const settingsStore = useWorkspaceSettingsStore()
const authStore = useAuthStore()
const candidateStore = useCandidateStore()

///////////////////////////////////////////////////////////////////////////////
// Tabs
///////////////////////////////////////////////////////////////////////////////

const tabs = [
  { label: 'Pending', status: 'matched' },
  { label: 'Approved', status: 'approved' },
  { label: 'Rejected', status: 'rejected' },
]
const current_index = ref(0)
const selectedTab = computed(() => tabs[current_index.value])

const selectTab = (index: number) => {
  current_index.value = index
}

///////////////////////////////////////////////////////////////////////////////
// Search
///////////////////////////////////////////////////////////////////////////////

const searchQuery = ref('')
const filteredConnections = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return connectionStore.connections
  return connectionStore.connections.filter((c: Connection) => {
    const name = get(c, 'candidate_investor.subject.name', '').toLowerCase()
    const email = get(c, 'candidate_investor.subject.email', '').toLowerCase()
    return name.includes(query) || email.includes(query)
  })
})

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const fetchCurrentTab = async (trigger_skeleton = false) => {
  if (trigger_skeleton) showSkeleton()
  await connectionStore.fetchConnections({ status: selectedTab.value.status, group_id: workspace.value.id as string })
  if (trigger_skeleton) hideSkeleton()
}

const inviteCandidate = async (connection: Connection) => {
  selectedConnection.value = connection
  approveModal.open()
}

const loadingAction = ref<{ id: string; type: string } | null>(null)

const confirmInvite = async () => {
  if (!selectedConnection.value || loadingAction.value) return
  loadingAction.value = { id: selectedConnection.value.id, type: 'invite' }
  approveModal.close()
  await connectionStore.inviteCandidate(selectedConnection.value.id)

  await fetchCurrentTab()
  loadingAction.value = null
  selectedConnection.value = null
}

const rejectCandidate = async (connection: Connection) => {
  selectedConnection.value = connection
  rejectModal.open()
}

const confirmReject = async () => {
  if (!selectedConnection.value || loadingAction.value) return
  loadingAction.value = { id: selectedConnection.value.id, type: 'reject' }
  rejectModal.close()
  await connectionStore.rejectCandidate(selectedConnection.value.id)

  current_index.value = 2
  await fetchCurrentTab()
  loadingAction.value = null
  selectedConnection.value = null
}

const restoreCandidate = async (connection: Connection) => {
  selectedConnection.value = connection
  restoreModal.open()
}

const confirmRestore = async () => {
  if (!selectedConnection.value || loadingAction.value) return
  loadingAction.value = { id: selectedConnection.value.id, type: 'restore' }
  restoreModal.close()
  await connectionStore.restoreCandidate(selectedConnection.value.id)

  current_index.value = 0
  await fetchCurrentTab()
  loadingAction.value = null
  selectedConnection.value = null
}

const viewDetail = (connection: Connection) => {
  selectedConnection.value = connection
  detailModal.open()
}

///////////////////////////////////////////////////////////////////////////////
// Helpers
///////////////////////////////////////////////////////////////////////////////

const getInitials = (name: string) => {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const getCountryName = (code: string) => {
  if (!code) return '—'
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
    return regionNames.of(code) || code
  } catch {
    return code
  }
}

const getInvestorStatus = (connection: Connection) => {
  if (connection.group_status === 'approved' && connection.investor_status === 'matched') return 'Invited'
  if (connection.group_status === 'approved' && connection.investor_status === 'accepted') return 'Accepted'
  if (connection.group_status === 'approved' && connection.investor_status === 'rejected') return 'Declined'
  return ''
}

const getInvestorStatusColor = (connection: Connection) => {
  if (connection.investor_status === 'accepted') return 'text-emerald-600'
  if (connection.investor_status === 'rejected') return 'text-red-500'
  return 'text-[#3982AF]'
}

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const workspace = computed(() => workspaceStore.current)
const approveModal = useModal()
const rejectModal = useModal()
const restoreModal = useModal()
const detailModal = useModal()
const dataInfoModal = useModal()
const selectedConnection = ref<Connection | null>(null)

const connectionsEnabled = computed(
  () => settingsStore.settings?.connections_matching_enabled && settingsStore.workspace?.connection_profile_complete,
)
const showWaitingState = computed(() => workspace.value.is_candidate_complete && !workspace.value.has_connections)
const myCandidate = computed(() => {
  const candidateId = workspace.value?.connecting_candidate_id
  return candidateId ? candidateStore.getCandidate(candidateId) : undefined
})

const previewGroupCandidate = computed((): GroupCandidateExpanded | undefined => {
  const ws = workspace.value
  const candidate = myCandidate.value
  if (!ws || !candidate) return undefined
  return {
    ...candidate,
    subject: {
      id: ws.id || '',
      name: ws.name || '',
      subdomain: ws.subdomain || '',
      logo_url: ws.logo_url || null,
    },
  } as GroupCandidateExpanded
})

watch(current_index, async () => {
  await fetchCurrentTab(true)
})

onMounted(async () => {
  showSkeleton()
  await Promise.all([workspaceStore.fetchCurrent(), settingsStore.fetch()])

  if (workspace.value?.connecting_candidate_id) {
    candidateStore.fetchCandidate(workspace.value.connecting_candidate_id)
  }

  await fetchCurrentTab()
  hideSkeleton()
})
</script>
<template>
  <ConnectionsLayout>
    <WelcomeComponent v-if="!connectionsEnabled || route.query.welcome !== undefined" />

    <!-- Loading -->
    <VLoadingSection
      v-else-if="skeleton"
      class="py-32"
      title="Loading your connections"
      description="Please wait a moment while we fetch your data."
    />

    <WaitingState v-else-if="showWaitingState" />
    <div v-else>
      <!-- ===== HERO BANNER ===== -->
      <ConnectionsHero
        title="Manage your"
        title-accent="investor connections."
        subtitle="Review, approve and manage investors that have been matched to your firm based on your profile."
      >
        <template #action>
          <div class="flex items-center gap-3">
            <button
              @click="dataInfoModal.open()"
              class="text-sm text-white/70 underline underline-offset-2 hover:text-white"
            >
              What investors see
            </button>
            <RouterLink :to="{ name: 'connecting.groups.edit' }">
              <VButton size="sm" variant="v-blue">
                <span class="flex items-center gap-1.5">
                  <VIcon name="Edit" class="h-3.5 w-3.5" />
                  Edit Profile
                </span>
              </VButton>
            </RouterLink>
          </div>
        </template>
      </ConnectionsHero>

      <!-- ===== TABLE CARD ===== -->
      <div
        class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md dark:border-zinc-700 dark:bg-zinc-900"
      >
        <!-- Toolbar -->
        <div class="flex flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-4 dark:border-zinc-800">
          <!-- Tab pills -->
          <div class="flex gap-1 rounded-full bg-gray-100 p-1 dark:bg-zinc-800">
            <button
              v-for="(tab, index) in tabs"
              :key="tab.status"
              @click="selectTab(index)"
              class="rounded-full px-4 py-1.5 text-[13px] font-medium transition-all"
              :class="
                index === current_index
                  ? 'bg-white font-semibold text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-white'
                  : 'text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200'
              "
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Search -->
          <div class="relative ml-auto max-w-[280px]">
            <VIcon name="Search" class="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search investors..."
              class="w-full rounded-full border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-[13px] outline-none transition-colors focus:border-[#3b88af] focus:bg-white dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:focus:border-[#3b88af]"
            />
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <!-- Skeleton loading state -->
          <div v-if="skeleton" class="p-8">
            <div class="space-y-4">
              <div v-for="i in 5" :key="i" class="flex items-center gap-4">
                <div class="h-10 w-10 animate-pulse rounded-full bg-gray-200 dark:bg-zinc-700"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-40 animate-pulse rounded bg-gray-200 dark:bg-zinc-700"></div>
                  <div class="h-3 w-56 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div>
                </div>
                <div class="h-6 w-20 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div>
                <div class="h-8 w-24 animate-pulse rounded bg-gray-100 dark:bg-zinc-800"></div>
              </div>
            </div>
          </div>

          <table v-else-if="filteredConnections.length > 0" class="w-full border-collapse">
            <thead>
              <tr>
                <th
                  class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                >
                  Name
                </th>
                <th
                  class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                >
                  Email
                </th>
                <th
                  class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                >
                  Country
                </th>
                <th
                  class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                >
                  Status
                </th>
                <th
                  class="border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <ConnectionInvestorRow
                v-for="connection in filteredConnections"
                :key="connection.id"
                :candidate="connection.candidate_investor"
                :linked="connection.group_status === 'approved'"
                :linkTo="{
                  name: 'investing.individual-overview',
                  params: {
                    slug: route.params.slug,
                    individual_id: connection.candidate_investor.subject_id,
                  },
                }"
              >
                <template #status>
                  <div class="flex flex-wrap items-center gap-2.5">
                    <AccreditationBadge
                      :status="
                        get(connection, 'candidate_investor.subject.accredited_status') ||
                        AccreditationStatuses.NotAccredited
                      "
                    />
                    <ScreeningCheckWrapper
                      v-if="get(connection, 'candidate_investor.subject.screening_check_id')"
                      :checkId="get(connection, 'candidate_investor.subject.screening_check_id')"
                      :status="get(connection, 'candidate_investor.subject.screening_check_status') as ScreeningStatus"
                    />
                    <VBadge
                      v-if="selectedTab.status === 'approved' && getInvestorStatus(connection)"
                      :color="
                        connection.investor_status === 'accepted'
                          ? 'green'
                          : connection.investor_status === 'rejected'
                            ? 'red'
                            : 'sky'
                      "
                      size="xxs"
                    >
                      {{ getInvestorStatus(connection) }}
                    </VBadge>
                  </div>
                </template>
                <template #actions>
                  <div class="flex items-center justify-end gap-2">
                    <!-- Pending tab: Approve + Reject -->
                    <template v-if="selectedTab.status === 'matched'">
                      <VButton
                        size="xs"
                        variant="v-blue"
                        @click="inviteCandidate(connection)"
                        :loading="loadingAction?.id === connection.id && loadingAction?.type === 'invite'"
                      >
                        Approve
                      </VButton>
                      <VButton
                        size="xs"
                        variant="v-red"
                        @click="rejectCandidate(connection)"
                        :loading="loadingAction?.id === connection.id && loadingAction?.type === 'reject'"
                      >
                        Reject
                      </VButton>
                    </template>

                    <!-- Rejected tab: Restore -->
                    <template v-if="selectedTab.status === 'rejected'">
                      <VButton
                        size="xs"
                        variant="secondary"
                        @click="restoreCandidate(connection)"
                        :loading="loadingAction?.id === connection.id && loadingAction?.type === 'restore'"
                      >
                        Restore
                      </VButton>
                    </template>
                  </div>
                </template>
              </ConnectionInvestorRow>
            </tbody>
          </table>

          <!-- Empty state -->
          <div v-else-if="!skeleton" class="py-12 text-center">
            <VIcon name="Users" :size="36" class="mx-auto mb-3 text-gray-300" />
            <p class="text-sm text-gray-500 dark:text-zinc-400">
              <template v-if="searchQuery">No investors match your search.</template>
              <template v-else-if="selectedTab.status === 'matched'">No pending connections yet.</template>
              <template v-else-if="selectedTab.status === 'approved'">No approved connections yet.</template>
              <template v-else>No rejected connections yet.</template>
            </p>
          </div>
        </div>
      </div>

      <!-- ===== APPROVE CONFIRMATION MODAL ===== -->
      <VPopUpModal :modalStore="approveModal">
        <div v-if="selectedConnection" class="text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3b88af]/10">
            <VIcon name="Check" :size="28" class="text-[#3b88af]" />
          </div>
          <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">Approve Connection</h3>
          <p class="mb-6 text-sm leading-relaxed text-gray-500">
            By approving,
            <span class="font-semibold text-gray-700 dark:text-gray-300">{{
              get(selectedConnection, 'candidate_investor.subject.name')
            }}</span>
            will gain access to your firm and all information shared with investors.
          </p>
          <div class="flex gap-3">
            <button
              @click="approveModal.close()"
              class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <VButton variant="v-blue" class="flex-1" @click="confirmInvite()" :loading="!!loadingAction">
              Grant Access
            </VButton>
          </div>
        </div>
      </VPopUpModal>

      <!-- ===== REJECT CONFIRMATION MODAL ===== -->
      <VPopUpModal :modalStore="rejectModal">
        <div v-if="selectedConnection" class="text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <VIcon name="X" :size="28" class="text-red-500" />
          </div>
          <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">Reject Connection</h3>
          <p class="mb-6 text-sm leading-relaxed text-gray-500">
            Are you sure you want to reject
            <span class="font-semibold text-gray-700 dark:text-gray-300">{{
              get(selectedConnection, 'candidate_investor.subject.name')
            }}</span
            >? They will be moved to the rejected list. You can restore them later if needed.
          </p>
          <div class="flex gap-3">
            <button
              @click="rejectModal.close()"
              class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <VButton variant="v-red" class="flex-1" @click="confirmReject()" :loading="!!loadingAction">
              Reject
            </VButton>
          </div>
        </div>
      </VPopUpModal>

      <!-- ===== RESTORE CONFIRMATION MODAL ===== -->
      <VPopUpModal :modalStore="restoreModal">
        <div v-if="selectedConnection" class="text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3b88af]/10">
            <VIcon name="ArrowNarrowLeft" :size="28" class="text-[#3b88af]" />
          </div>
          <h3 class="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">Restore Connection</h3>
          <p class="mb-6 text-sm leading-relaxed text-gray-500">
            Are you sure you want to restore
            <span class="font-semibold text-gray-700 dark:text-gray-300">{{
              get(selectedConnection, 'candidate_investor.subject.name')
            }}</span
            >? They will be moved back to the pending list for review.
          </p>
          <div class="flex gap-3">
            <button
              @click="restoreModal.close()"
              class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <VButton variant="v-blue" class="flex-1" @click="confirmRestore()" :loading="!!loadingAction">
              Restore
            </VButton>
          </div>
        </div>
      </VPopUpModal>

      <!-- ===== DATA SHARING INFO MODAL ===== -->
      <VPopUpModal :modalStore="dataInfoModal" widthClass="w-3/4 max-w-4xl">
        <div>
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#3b88af]/10">
            <VIcon name="InfoCircleFilled" :size="28" class="text-[#3b88af]" />
          </div>
          <h3 class="mb-6 text-center text-xl font-bold text-gray-900 dark:text-gray-200">What Investors Can See</h3>

          <div class="mb-6 grid grid-cols-2 gap-6">
            <!-- Left: Data list -->
            <div>
              <div class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">Shared Data</div>
              <div class="mb-4">
                <div class="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">Firm Information</div>
                <ul class="ml-4 list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <li>Firm name</li>
                  <li>Logo</li>
                  <li>Email</li>
                </ul>
              </div>
              <div class="mb-4">
                <div class="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">Investment Profile</div>
                <ul class="ml-4 list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <li>Investment types (Fund, Syndicate, Angel Group)</li>
                  <li>Minimum investment amounts</li>
                  <li>Investment stages</li>
                  <li>Industries of focus</li>
                  <li>University affiliations</li>
                  <li>Investment thesis</li>
                  <li>Team members</li>
                </ul>
              </div>
            </div>

            <!-- Right: Card preview -->
            <div v-if="previewGroupCandidate">
              <div class="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">Preview</div>
              <ConnectionGroupCard :candidate="previewGroupCandidate" />
            </div>
          </div>

          <p
            class="mb-6 rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-500 dark:bg-gray-700 dark:text-gray-400"
          >
            Investors can only see this information after you invite them.
          </p>

          <button
            @click="dataInfoModal.close()"
            class="w-full rounded-lg bg-[#3b88af] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#3b88af]/90"
          >
            Got it
          </button>
        </div>
      </VPopUpModal>

      <!-- ===== VIEW DETAIL MODAL ===== -->
      <VModal :modalStore="detailModal" :has_footer="false">
        <template #main>
          <div v-if="selectedConnection">
            <h3 class="mb-5 text-lg font-bold text-gray-900">Investor Profile</h3>

            <!-- Investor header -->
            <div class="mb-5 flex items-center gap-4 rounded-xl bg-gray-50 p-4 dark:bg-zinc-800">
              <VAvatar
                :image="null"
                :initials="getInitials(get(selectedConnection, 'candidate_investor.subject.name', ''))"
                size="lg"
              />
              <div>
                <div class="text-lg font-bold text-gray-900 dark:text-white">
                  {{ get(selectedConnection, 'candidate_investor.subject.name') }}
                </div>
                <div class="text-sm text-gray-500 dark:text-zinc-400">
                  {{ get(selectedConnection, 'candidate_investor.subject.email') }}
                </div>
              </div>
            </div>

            <!-- Info rows -->
            <div class="divide-y divide-gray-100 dark:divide-zinc-800">
              <div class="flex items-center justify-between py-3">
                <span class="text-sm font-medium text-gray-500">Accreditation</span>
                <AccreditationBadge
                  :status="
                    get(selectedConnection, 'candidate_investor.subject.accredited_status') ||
                    AccreditationStatuses.NotAccredited
                  "
                />
              </div>
              <div class="flex items-center justify-between py-3">
                <span class="text-sm font-medium text-gray-500">Country</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ getCountryName(get(selectedConnection, 'candidate_investor.subject.country', '')) }}
                </span>
              </div>
              <div
                v-if="get(selectedConnection, 'candidate_investor.industries_interested', []).length > 0"
                class="flex items-start justify-between py-3"
              >
                <span class="text-sm font-medium text-gray-500">Industries</span>
                <div class="flex flex-wrap justify-end gap-1">
                  <VBadge
                    v-for="industry in get(selectedConnection, 'candidate_investor.industries_interested', [])"
                    :key="industry"
                    color="v-green"
                    size="xxs"
                  >
                    {{ industry }}
                  </VBadge>
                </div>
              </div>
              <div
                v-if="get(selectedConnection, 'candidate_investor.investment_types_interested', []).length > 0"
                class="flex items-start justify-between py-3"
              >
                <span class="text-sm font-medium text-gray-500">Investment Types</span>
                <div class="flex flex-wrap justify-end gap-1">
                  <VBadge
                    v-for="type in get(selectedConnection, 'candidate_investor.investment_types_interested', [])"
                    :key="type"
                    color="v-blue"
                    size="xxs"
                  >
                    {{ TYPE_LABELS[type] || type }}
                  </VBadge>
                </div>
              </div>
              <div
                v-if="get(selectedConnection, 'candidate_investor.fund_investment_range')"
                class="flex items-center justify-between py-3"
              >
                <span class="text-sm font-medium text-gray-500">Fund Investment Range</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ get(selectedConnection, 'candidate_investor.fund_investment_range') }}
                </span>
              </div>
              <div class="flex items-center justify-between py-3">
                <span class="text-sm font-medium text-gray-500">LinkedIn</span>
                <a
                  v-if="get(selectedConnection, 'candidate_investor.subject.linkedin_url')"
                  :href="get(selectedConnection, 'candidate_investor.subject.linkedin_url')"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm font-semibold text-[#0077b5] hover:underline"
                >
                  {{ get(selectedConnection, 'candidate_investor.subject.linkedin_url') }}
                </a>
                <span v-else class="text-sm text-gray-400">Not provided</span>
              </div>
              <div v-if="getInvestorStatus(selectedConnection)" class="flex items-center justify-between py-3">
                <span class="text-sm font-medium text-gray-500">Invitation Status</span>
                <span class="text-sm font-semibold" :class="getInvestorStatusColor(selectedConnection)">
                  {{ getInvestorStatus(selectedConnection) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </VModal>
    </div>
  </ConnectionsLayout>
</template>
